"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Pencil, Plus, Users, X } from "lucide-react";
import { cn, withAlpha, TEAM_COLORS, type Employee, type Team } from "@routecore/core";
import { Modal, ModalHeader } from "@routecore/modal";
import { ConfirmDelete, PrimaryButton, inputCls } from "@routecore/controls";

export type TeamFormData = { name: string; color: string };

export default function TeamModal({
  open,
  teams,
  employees,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}: {
  open: boolean;
  teams: Team[];
  employees: Employee[];
  onClose: () => void;
  onCreate: (data: TeamFormData) => void;
  onUpdate: (id: number, data: TeamFormData) => void;
  onDelete: (id: number) => void;
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(TEAM_COLORS[0]);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setEditingId(null);
    setColor(TEAM_COLORS.find((c) => !teams.some((t) => t.color === c)) ?? TEAM_COLORS[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (editingId) onUpdate(editingId, { name: trimmed, color });
    else onCreate({ name: trimmed, color });
    setName("");
    setEditingId(null);
  };

  const startEdit = (team: Team) => {
    setEditingId(team.id);
    setName(team.name);
    setColor(team.color);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setName("");
  };

  const memberCount = (teamId: number) => employees.filter((e) => e.teamId === teamId).length;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader
        eyebrow={editingId ? "Rename squad" : "Squads"}
        title={editingId ? "Edit team" : "Manage teams"}
        onClose={onClose}
      />

      {/* existing teams */}
      <div className="max-h-[38vh] space-y-1.5 overflow-y-auto pr-1">
        <AnimatePresence initial={false}>
          {teams.map((t) => (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.22 }}
              className={cn(
                "group flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors",
                editingId === t.id ? "border-accent bg-accent/[0.06]" : "border-line bg-paper/60",
              )}
            >
              <span
                className="grid size-8 shrink-0 place-items-center rounded-lg"
                style={{ backgroundColor: withAlpha(t.color, 0.16), color: t.color }}
              >
                <Users className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold">{t.name}</div>
                <div className="text-[11px] text-ink-faint">
                  {memberCount(t.id)} {memberCount(t.id) === 1 ? "member" : "members"}
                </div>
              </div>
              <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  title="Rename"
                  onClick={() => startEdit(t)}
                  className="grid size-7 place-items-center rounded-md text-ink-faint transition-colors hover:bg-ink/10 hover:text-ink"
                >
                  <Pencil className="size-3.5" />
                </button>
                <ConfirmDelete onConfirm={() => onDelete(t.id)} />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {teams.length === 0 && (
          <div className="grid place-items-center gap-2 rounded-2xl border border-dashed border-line py-8 text-center">
            <Users className="size-5 text-ink-faint" />
            <p className="text-sm text-ink-soft">No teams yet — create your first squad below.</p>
          </div>
        )}
      </div>

      {/* create / edit form */}
      <div className="mt-4 rounded-2xl border border-line bg-porcelain p-3.5">
        <span className="mb-2 block text-[11px] font-bold tracking-[0.14em] text-ink-soft uppercase">
          {editingId ? "Rename team" : "New team"}
        </span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="e.g. Field Operations"
          className={inputCls}
        />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {TEAM_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              title={c}
              className={cn(
                "grid size-7 place-items-center rounded-full transition-all active:scale-90",
                color === c
                  ? "scale-110 ring-2 ring-ink ring-offset-2 ring-offset-porcelain"
                  : "hover:scale-110",
              )}
              style={{ backgroundColor: c }}
            >
              {color === c && <Check className="size-3.5 text-white" />}
            </button>
          ))}
        </div>
        <div className="mt-3.5 flex items-center gap-2">
          <PrimaryButton
            onClick={submit}
            icon={editingId ? Check : Plus}
            disabled={!name.trim()}
            className="flex-1 justify-center"
          >
            {editingId ? "Save team" : "Add team"}
          </PrimaryButton>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2.5 text-sm font-semibold text-ink-soft transition-colors hover:text-ink"
            >
              <X className="size-3.5" /> Cancel
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
